import { version } from '@gen/version';
import { removeChildren, isIndexed, escapeHtml } from '@p4b/utils';
import { cleanUpOldExam, runExam, ExamState, deleteCurrentExam } from '@p4b/exam-viewer';
import { dbGet, dbClearLogin, dbClearSelect, dbPut, dbCount } from '@p4b/utils-db';
import { setCredentials, clearCredentials, HttpError, getText, setUUID, enableRemoteLogging, currentDomain, getOrg } from '@p4b/utils-net';
import { Json, StoreChunks, deleteExam, getExamList, State, ExamMap, RemoteExamItem, Structure, deleteUnavailable, fetchExams, getLocalExams, updateExamPins, splitExamId, getFetchList, ExamError, ValidationError } from '@p4b/exam-service';
import { DecryptionError } from './utils-zip';
import { AssignmentViewer, ChosenExam, Logout } from '@p4b/assignment-viewer';
import { Auth } from '@p4b/utils-login';
import { Progress } from '@p4b/utils-progress';
import { alertModal, errorModal } from "@p4b/modal-dialog";
import { initTranslation, translate, localise, setLanguage, getLanguages, getCurrentLanguage } from '@p4b/utils-lang';
import { faLanguage } from '@fortawesome/free-solid-svg-icons';
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import { DOMcreateElement } from '@p4b/utils-jsx';

function formatErrorMessage(err: unknown): string {
    if (err instanceof ExamError || err instanceof ValidationError) {
        return err.message;
    }
    if (err instanceof Error) {
        return escapeHtml(err.message);
    }
    return escapeHtml(String(err));
}
import { v4 as genUuid } from 'uuid';
import { Dropdown } from '@p4b/component-dropdown';

declare global {
    interface Window {
        webkit: {
            messageHandlers: {
                iOS: {
                    postMessage: Json;
                }
            }
        }
    }
    interface Document {
        msHidden: boolean|undefined;
        webkitHidden: boolean|undefined;
    }
}

window.addEventListener('contextmenu', canvasContextmenuHandler, {passive: false});

function canvasContextmenuHandler(event: MouseEvent): boolean {
    event.preventDefault();
    event.stopPropagation();
    return false;
}


//----------------------------------------------------------------------------
// Handle multiple open tabs.

if (window.BroadcastChannel) {
    // If BroadcastChannel available, then when new p4b tab opened, pause other
    // p4b tabs already open with modal dialog and reload when dialog closed.
    const bc = new BroadcastChannel('p4b');
    bc.onmessage = async (e) => {
        if (e.data === 'open') {
            await initTranslation();
            await alertModal(translate('P4B_BACKGROUNDED'));
            window.location.reload();
        }
    };
    bc.postMessage('open');
} else {
    // If BroadcastChannel not available, reload p4b tabs when transitioning
    // from not-visible to visible.
    let visibilityChange: string|undefined;
    let isHidden: (() => boolean)|undefined;

    if (document.hidden !== undefined) { // Opera 12.10 and Firefox 18 and later support
        isHidden = () => document.hidden;
        visibilityChange = "visibilitychange";
    } else if (document.msHidden !== undefined) {
        isHidden = () => document.msHidden as boolean;
        visibilityChange = "msvisibilitychange";
    } else if (document.webkitHidden !== undefined) {
        isHidden = () => document.webkitHidden as boolean;
        visibilityChange = "webkitvisibilitychange";
    }

    if (visibilityChange) {
        window.addEventListener(visibilityChange, () => {
            if (isHidden?.() === false) {
                window.location.reload();
            }
        });
    }
}

//----------------------------------------------------------------------------\
// Single Application Mode for risr/browser lockdown

/*
function singleApplicationModeStartOnSuccess(): void {
    //alertModal('Single application mode started');
    SAM = true;
}

function singleApplicationModeStartOnError(): void {
    //alertModal('Single application mode not available');
}

function singleApplicationModeExitOnSuccess(): void {
    //alertModal('Single application mode exited');
    SAM = false;
}

function singleApplicationModeExitOnError(): void {
    //alertModal('Single application mode exit failed');

}
*/

function singleApplicationModeStart(): void {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.iOS) {
        //window.webkit.messageHandlers.iOS.postMessage({
        //    type: 'sam-start',
        //    succ: "singleApplicationModeStartOnSuccess()",
        //    fail: "singleApplicationModeStartOnError()"
        //});
    }
}

function singleApplicationModeExit(): void {
    if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.iOS) {
        //window.webkit.messageHandlers.iOS.postMessage({
        //    type: "sam-exit",
        //    succ: "singleApplicationModeExitOnSuccess()",
        //    fail: "singleApplicationModeExitOnError()"
        //});
    }
}

//----------------------------------------------------------------------------
// Initialize Welcome UI

class WelcomeUi {
    content: HTMLElement;
    welcomePage: HTMLElement;
    version: HTMLElement | null;
    welcomePanel: HTMLElement;
    language: HTMLElement;
    languageSelect: Dropdown

    constructor() {
        this.version = document.getElementById('version-alert');
        this.welcomePanel = <div className="choose-exam"/>;
        this.language = <div className="language-dropdown-panel" aria-label="Change language" style={{backgroundColor: '#00000030'}}/>;
        this.languageSelect = new Dropdown({
            name: 'answer',
            parent: this.language,
            disabled: false,
            menuIcon: faLanguage
        });
        this.welcomePage = <div className="welcome-viewport">
            <div className="welcome-panel">
                <div style={{flex: '0.9', display: 'flex', flexDirection: 'column', inlineSize: '100%'}}>
                    <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', inlineSize: '100%'}}>
                        {this.language}
                        <div style={{paddingInlineEnd: '8px', paddingBlockStart: '8px', fontSize: '0.75rem', color: 'rgba(255,255,255,0.7)', whiteSpace: 'break-spaces'}}>{version}</div>
                    </div>
                </div>
                {this.welcomePanel}
                <div style={{flex: '1.1', inlineSize: '100%'}}/>
            </div>
        </div>;

        const languages = getLanguages();
        for (const language of languages) {
            this.languageSelect.addOption(language);
        }
        this.languageSelect.selectByIndex(getCurrentLanguage());
        this.setLangAttribute();
        this.language.addEventListener('dropdown-select',this.handleSelect)

        const content = document.getElementById('content')
        if (!content) {
            throw new Error('Content element not found');
        }
        this.content = content;
        if (this.content.children.length > 0) {
            removeChildren(this.content);
        }
        this.content.appendChild(this.welcomePage);
    }

    destroy() {
        this.language.removeEventListener('dropdown-select', this.handleSelect);
    }

    private readonly handleSelect = async (e: Event) => {
        console.debug('EVENT', e)
        if (e instanceof CustomEvent && typeof e.detail === 'number') {
            await setLanguage(e.detail);
            location.reload();
            this.setLangAttribute();
        }
    }

    private setLangAttribute(): void {
        const [lang] = translate('LOCALE').split('-');
        document.documentElement.setAttribute('lang', lang);
        document.documentElement.setAttribute('xml:lang', lang);
        window.parent?.document?.documentElement?.setAttribute('lang', lang);
    }
}

//----------------------------------------------------------------------------
// Global State

const state: State = {
    candidateId: ''
}

//----------------------------------------------------------------------------
// Initialise Image Store

const IMAGE_STORE = new StoreChunks(0x1000000, 0x1000000);

//----------------------------------------------------------------------------
// Credentials

interface Credentials {
    username: string;
    password: string;
}

function isCredentials(cred: unknown): cred is Credentials {
    return isIndexed(cred) &&
        typeof cred.username === 'string' &&
        typeof cred.password === 'string';
}

async function savedCredentials():  Promise<Credentials|null> {
    const savedCreds = await dbGet('users', 'credentials');
    if (isCredentials(savedCreds)) {
        state.candidateId = savedCreds.username;
        state.candidatePin = savedCreds.password;
        setCredentials({
            loginId: savedCreds.username,
            loginPwd: savedCreds.password,
        });
        return savedCreds;
    }
    return null;
}

async function login(wi: WelcomeUi, savedCreds: Credentials|null): Promise<RemoteExamItem[]> {
    document.title = '𝗿𝗶𝘀𝗿/assess - Login';
    if (window.parent?.document) window.parent.document.title = document.title;
    await dbPut('users', 0, 'examCount');
    let loginError = '';
    let examList: RemoteExamItem[] = [];
    removeChildren(wi.welcomePanel);
    const auth = new Auth(wi.welcomePanel);
    while (true) {
        try {
            removeChildren(wi.welcomePanel);
            const credentials = await auth.login({error: loginError});
            state.candidateId = credentials.username;
            state.candidatePin = credentials.password;
            loginError = '';
            setCredentials({
                loginId: credentials.username,
                loginPwd: credentials.password,
            });
            wi.welcomePanel.innerHTML = `<p class="progress-subtext">Fetching available exams...</p>`;
            const {exams} = await getExamList();
            examList = exams ?? [];
            auth.destroy();
            wi.welcomePanel.innerHTML = `<p class="progress-subtext">Saving candidate credentials...</p>`;
            await dbPut('users', {
                username: state.candidateId,
                password: state.candidatePin,
            }, 'credentials');
            return examList;
        } catch (err) {
            if (err instanceof HttpError) {
                if (err.status == 403) {
                    loginError = translate('ERROR_USER_PASS');
                } else {
                    loginError = translate('ERROR_HTTP', {code: localise(err.status)});
                }
            } else {
                loginError = translate('ERROR_UNKNOWN', {message: String(err)});
            }
        }
    }
}

//----------------------------------------------------------------------------
// Exam Synchronization

async function loadExams(wi: WelcomeUi, remoteExamList: RemoteExamItem[], localExamMap: ExamMap): Promise<ExamMap> {
    wi.welcomePanel.innerHTML = `<p class="progress-subtext">Removing finished exams...</p>`;
    await deleteUnavailable(localExamMap, remoteExamList);
    wi.welcomePanel.innerHTML = `<p class="progress-subtext">Updating current exams...</p>`;
    await updateExamPins(localExamMap, remoteExamList);
    wi.welcomePanel.innerHTML = `<p class="progress-subtext">Fetching new exams...</p>`;
    const fetchList = getFetchList(localExamMap, remoteExamList);
    if (fetchList.length > 0) {
        removeChildren(wi.welcomePanel);
        retry: while (true) {
            const progress = new Progress(wi.welcomePanel);
            try {
                await fetchExams(localExamMap, fetchList, progress);
                break retry;
            } catch(err) {
                console.error(String(err));
                const opt = await errorModal({
                    title: translate('ERROR_DOWNLOAD'),
                    message: formatErrorMessage(err),
                    primary: {retry: translate('ALERT_RETRY')},
                    other: {ignore: translate('ALERT_IGNORE')},
                });
                switch (opt) {
                    case 'retry':
                        break;
                    case 'ignore':
                        break retry;
                }
            } finally {
                progress.destroy();
            }
        }
    } else {
        console.debug('NO EXAMS TO FETCH');
    }
    return localExamMap;
}

//----------------------------------------------------------------------------
// Exam Selection

async function examCount(): Promise<number> {
    const count = await dbGet('users', 'examCount');
    if (typeof count === 'number') {
        return count;
    }
    return 0;
}

async function getSavedChoice():  Promise<string|null> {
    const savedChoice= await dbGet('users', 'choice');
    console.debug('SAVED_CHOICE', savedChoice);
    if (typeof savedChoice === 'string') {
        return savedChoice;
    }
    return null;
}

async function chooseExam(wi: WelcomeUi, exams: ExamMap, examId: string|null, pin: string|null, proctored: boolean, savedExamCount: number): Promise<ChosenExam|Logout> {
    document.title = '𝗿𝗶𝘀𝗿/assess - Choose Exam';
    if (window.parent?.document) window.parent.document.title = document.title;

    await dbClearSelect(); // new exam clear old just in case

    if (examId && pin) {
        console.debug('PIN', pin, "EXAM", examId);
        if (exams.has(examId)) {
            return {
                kind: 'exam',
                examId,
                examPin: pin,
                autoPin: true,
            };
        }
    } else {
        if ((typeof savedExamCount !== 'number' || savedExamCount === 0) && !state.badPin) {
            if (exams.size === 1) {
                const exam = exams.entries().next().value;
                if (exam) {
                    const [key, {pin, proctored: examProctored}] = exam;
                    if (pin && (!examProctored || proctored)) {
                        return {
                            kind: 'exam',
                            examId: key,
                            examPin: pin,
                            autoPin: true,
                        }
                    }
                }
            }
        }
    }

    let serverVersion = undefined
    try {
        serverVersion = await getText(currentDomain, '/status?format=version');
        await dbPut('users', serverVersion, 'serverVersion');
    } catch (err) {
        console.warn(String(err));
        try {
            const savedVersion = await dbGet('users', 'serverVersion');
            if (typeof savedVersion === 'string') {
                serverVersion = savedVersion;
            }
        } catch (err2) {
            console.error(String(err2));
        }
    }
    console.info('SERVER_VERSION', serverVersion);
    removeChildren(wi.welcomePanel);
    const org = await getOrg();
    const assignmentViewer = new AssignmentViewer({
        parent: wi.welcomePanel,
        badPin: state.badPin ?? '',
        //badCid: state.badCid === true,
        examPin: state.examPin,
        autoPin: state.autoPin,
        candidateId: state.candidateId,
        exams: exams,
        chosenExamId: state.chosenExamId,
        manualStart: (serverVersion !== undefined) && /^8.0/.test(serverVersion),
        proctored,
        org,
    });
    const choice = await assignmentViewer.getExamChoice();
    assignmentViewer.destroy();
    return choice;
}

//----------------------------------------------------------------------------
// Exam Preparation

interface Manifest {
    manifest: PractiqueNet.ExamJson;
    structure: Structure[];
}

function isSavedManifest(savedManifest: unknown): savedManifest is Manifest {
    return isIndexed(savedManifest)
        && typeof savedManifest.manifest === 'object'
        && typeof savedManifest.structure === 'object';
}

async function savedManifest(): Promise<Manifest|null> {
    const savedManifest = await dbGet('users', 'manifest');
    if (isSavedManifest(savedManifest)) {
        return savedManifest;
    }
    return null;
}

interface SavedExam {
    examId: string;
    pin: string;
    proctored: boolean;
    hasProctorScript: boolean;
}

function isSavedExam(savedExam: unknown): savedExam is SavedExam {
    return isIndexed(savedExam)
        && typeof savedExam.examId === 'string'
        && typeof savedExam.pin === 'string'
        && typeof savedExam.proctored === 'boolean';
}

async function getSavedExam(): Promise<SavedExam|null> {
    const savedExam = await dbGet('users', 'exam');
    if (isSavedExam(savedExam)) {
        return savedExam;
    }
    return null;
}

async function prepareExam(wi: WelcomeUi, exams: ExamMap, imageStore: StoreChunks): Promise<Manifest> {
    console.debug('PREPARE EXAM');
    if (state.examPin == null) {
        throw 'EXAM_PIN is null';
    }
    if (state.chosenExamId == null) {
        throw 'CHOSEN_EXAM_ID is null';
    }
    if (state.candidateId == null) {
        throw 'CID is null';
    }
    console.debug('GET EXAM', exams, state.chosenExamId);
    const examIndex = exams.get(state.chosenExamId);
    if (examIndex == null) {
        throw 'EXAM_INDEX is null'
    }
    console.debug('GOT EXAM', state.chosenExamId);
    removeChildren(wi.welcomePanel);
    const progress = new Progress(wi.welcomePanel);
    try {
        const manifestAndStructure =  await imageStore.unpackExam(
            state.examPin,
            splitExamId(state.chosenExamId),
            state.candidateId,
            examIndex.size,
            examIndex.full_title,
            progress
        );
        wi.welcomePanel.innerHTML = `<p class="progress-subtext">Saving current exam...</p>`;
        await dbPut('users', {
            examId: state.chosenExamId,
            pin: state.examPin,
            proctored: examIndex.proctored ?? false
        }, 'exam');
        wi.welcomePanel.innerHTML = `<p class="progress-subtext">Saving exam details...</p>`;
        await dbPut('users', manifestAndStructure, 'manifest');
        return manifestAndStructure;
    } finally {
        progress.destroy();
    }
}

//----------------------------------------------------------------------------
// Exam Start

// stay in exam until we are sure answers are uploaded
async function exam(wi: WelcomeUi, exams: ExamMap, meta: PractiqueNet.ExamJson, structure: Structure[], imageStore: StoreChunks): Promise<void> {
    console.debug('EXAM LAUNCHER', state.candidateId, meta.exam);
    try {
        singleApplicationModeStart();
        if (wi.content && meta.exam.answer_aes_key && state.candidateId && state.chosenExamId) {
            console.debug('LAUNCH EXAM');
            const exam = exams.get(state.chosenExamId);
            meta.exam.demo ||= exam?.demo;
            const context = {
                content: wi.content,
                structure,
                imageStore,
                candidateId: state.candidateId,
                candidateDetails: meta.candidateDetails,
                factorDetails: meta.factorDetails,
                component: exam?.component,
                meta: meta.exam,
                variantNames: [...(meta.variantDefault ? [meta.variantDefault] : []), ...(meta.variantNames ?? [])],
                onDestroy: async () => {
                    if (state.candidateId) {
                        try {
                            await deleteExam(exams, state.chosenExamId, true);
                        } catch (err) {
                            console.warn(err);
                        }
                    }
                }
            };
            wi.welcomePanel.innerHTML = `<p class="progress-subtext">Synchronise exam state...</p>`;
            const examState = await cleanUpOldExam(context);
            if ((examState.state === ExamState.Waiting || examState.state === ExamState.Stopped || examState.state === ExamState.Deleted) && examState.unsubmitted.length === 0) {
                wi.welcomePanel.innerHTML = `<p class="progress-subtext">Removing finished exam content...</p>`;
                await deleteCurrentExam();
                if (examState.state === ExamState.Waiting) {
                    return;
                } else {
                    await deleteExam(exams, state.chosenExamId, true);
                }
            }
            wi.welcomePanel.innerHTML = `<p class="progress-subtext">Starting exam viewer...</p>`;
            if (!(examState.state === ExamState.Waiting || examState.state === ExamState.Stopped || examState.state === ExamState.Deleted)) {
                removeChildren(wi.welcomePanel);
                await runExam(examState, context);
            }
        }
    } finally {
        state.chosenExamId = undefined;
        state.examPin = undefined;
        singleApplicationModeExit();
        if (wi.content != null) {
            removeChildren(wi.content);
            wi.content.appendChild(wi.welcomePage);
        }
    }
}

//----------------------------------------------------------------------------
// Utility Functions

function handleKeydown(event: KeyboardEvent): void {
    if (event.key === 'r' && event.ctrlKey) {
        event.preventDefault();
        event.stopPropagation();
        document.removeEventListener('keydown', handleKeydown);
        window.location.reload();
    }
}

async function getOrCreateDeviceId(): Promise<string> {
    const deviceId = await dbGet('session', 'device-id')
    if (typeof deviceId == 'string' && deviceId) {
        setUUID({uuid: deviceId});
        return deviceId;
    } else {
        const uuid = genUuid();
        await dbPut('session', uuid, 'device-id');
        setUUID({uuid});
        return uuid;
    }
}

async function logFreeStorage(): Promise<void> {
    // eslint-disable-next-line compat/compat
    const estimate = await navigator?.storage?.estimate?.();
    if (estimate && estimate.quota && estimate.usage) {
        console.info(`AVAILABLE_DISKSPACE ${Math.floor(estimate.usage / 1048576)} / ${Math.floor(estimate.quota / 1048576)} MB`);
    }
}

async function checkLatestVersion(wi: WelcomeUi): Promise<void> {
    try {
        const latest = await getText('', '/html5/version.txt');
        if (version.trim() !== latest.trim() && wi.version) {
            wi.version.innerHTML = translate('NEWER_VERSION_AVAILABLE', {version, latest});
            wi.version.hidden = false;
        }
    } catch (error) {
        console.warn('Error checking latest version:', error);
    }
}

async function getProctorScriptInDb(): Promise<boolean> {
    const count = await dbCount('users', 'p4bProctorRedirectScript');
    if (typeof count === 'number') {
        return count > 0;
    }
    return false;
}

function isProctorActive(params: URLSearchParams, hasProctorScript: boolean): boolean {
    /**
     * Check if exam is proctor flag is set and the redirect script is present if needed.
     */
    const isScriptInjected = window.parent.document.getElementById('proctor') !== null;
    return params.has('proctored') && (!hasProctorScript || isScriptInjected);
}

//----------------------------------------------------------------------------
// Main Flow

window.onpageshow = () => {
    if (window.SafeExamBrowser != null && (window.SafeExamBrowser.security.browserExamKey == ':' || window.SafeExamBrowser.security.configKey == ':')) {
        window.SafeExamBrowser.security.updateKeys(main);
    } else {
        main();
    }
}

async function main(): Promise<void> {
    try {
        const deviceId = await getOrCreateDeviceId();
        await enableRemoteLogging();
        console.info('DEVICE_ID', deviceId);
        console.info('P4B VERSION', version);
        await logFreeStorage();
        document.addEventListener('keydown', handleKeydown);
        await initTranslation();
        const wi = new WelcomeUi();
        checkLatestVersion(wi); // do not await
        const urlParams = new URLSearchParams((window.top ?? window).location.search);
        if (urlParams.get('logout') != null) {
            wi.welcomePanel.innerHTML = `<p class="progress-subtext">Logging out...</p>`;
            (window.top ?? window).location.href = (window.top ?? window).location.href.split("?")[0];
            return;
        }
        wi.welcomePanel.innerHTML = `<p class="progress-subtext">Resuming previous session...</p>`;
        while(true) {
            const savedChoicePromise = getSavedChoice();
            const examMapPromise = getLocalExams();
            const credentialsPromise = savedCredentials();
            const manifestPromise = savedManifest();
            const countPromise = examCount();
            const savedExamPromise = getSavedExam();
            const proctorScriptInDbPromise = getProctorScriptInDb();
            let [
                savedChoice,
                credentials,
                examMap,
                manifestAndStructure,
                count,
                savedExam,
                proctorScriptInDb,
            ] = await Promise.all([
                savedChoicePromise,
                credentialsPromise,
                examMapPromise,
                manifestPromise,
                countPromise,
                savedExamPromise,
                proctorScriptInDbPromise,
            ]);
            let remoteExamList: RemoteExamItem[]|null = null;
            if (credentials === null) {
                remoteExamList = await login(wi, credentials);
            }
            let choice: ChosenExam|Logout|null = null;
            const proctorActive = isProctorActive(urlParams, proctorScriptInDb);
            if (!(savedExam && (!savedExam.proctored || proctorActive))) {
                if (remoteExamList === null) {
                    const {exams} = await getExamList();
                    remoteExamList = exams ?? [];
                }
                console.debug(remoteExamList);
                console.debug(examMap);
                examMap = await loadExams(wi, remoteExamList, examMap);
                console.debug('SAVED EXAM', savedChoice);
                if (savedChoice && count === 0 && !state.badPin) {
                    const exam = examMap.get(savedChoice);
                    if (exam && exam.pin && (!exam.proctored || proctorActive)) {
                        choice = {
                            kind: 'exam',
                            examId: savedChoice,
                            examPin: exam.pin,
                            autoPin: true,
                        };
                    }
                }
                console.debug('CHOICE', choice);
                if (!choice) {
                    choice = await chooseExam(
                        wi,
                        examMap,
                        urlParams.get('exam'),
                        urlParams.get('pin'),
                        urlParams.has('proctored'),
                        count,
                    );
                }
            } else {
                choice = {
                    kind: 'exam',
                    examId: savedExam.examId,
                    examPin: savedExam.pin,
                    autoPin: true,
                }
            }
            if (choice.kind === 'exam') {
                state.chosenExamId = choice.examId;
                state.examPin = choice.examPin;
                state.autoPin = choice.autoPin;
                state.badPin = '';
                try {
                    if (manifestAndStructure === null) {
                        manifestAndStructure = await prepareExam(wi, examMap, IMAGE_STORE);
                    }
                    await exam(
                        wi,
                        examMap,
                        manifestAndStructure.manifest,
                        manifestAndStructure.structure,
                        IMAGE_STORE
                    );
                    if (examMap.size === 0) {
                        state.examPin = undefined;
                        state.candidateId = '';
                        state.candidatePin = undefined;
                        clearCredentials();
                        await dbClearLogin();
                        window.parent.postMessage({ action: 'stop-proctoring' }, '*');
                        (window.top ?? window).location.search = '?logout';
                        return;
                    }
                    break;
                } catch (err) {
                    console.error(err);
                    if (err instanceof DecryptionError) {
                        state.badPin = translate('ERROR_PIN'); //true;
                    } else {
                        state.badPin = formatErrorMessage(err);
                    }
                }
            } else if (choice.kind === 'logout') {
                state.examPin = undefined;
                state.candidateId = '';
                state.candidatePin = undefined;
                clearCredentials();
                await dbClearLogin();
                window.parent.postMessage({ action: 'stop-proctoring' }, '*');
                (window.top ?? window).location.search = '?logout';
                return;
            }
        }
        window.location.reload();
    } catch (err) {
        console.error(String(err));
        const opt = await errorModal({
            message: formatErrorMessage(err),
            primary: {retry: translate('ALERT_RETRY')},
            other: {quit: translate('ALERT_QUIT')},
        });
        if (opt === 'quit') {
            state.examPin = undefined;
            state.candidateId = '';
            state.candidatePin = undefined;
            clearCredentials();
            await dbClearLogin();
            window.parent.postMessage({ action: 'stop-proctoring' }, '*');
            (window.top ?? window).location.search = '?logout';
        } else {
            window.location.reload();
        }
    }
}
